import React, { useMemo } from 'react';
import Table from '../common/Table';
import { convertFromFirestoreTimestamp } from '../../utils/firestore-timestamp';
import { Chip } from '@mui/material';

export const StatusCell = ({ value }) => {
    switch (value.toLowerCase()) {
        case 'canceled':
            return <Chip color="error" label="Canceled" size="small" variant="light" />;
        case 'active':
            return <Chip color="success" label="Active" size="small" variant="light" />;
        case 'upcoming':
            return <Chip color="info" label="Upcoming" size="small" variant="light" />;
        case 'expired':
            return <Chip color="warning" label="Expired" size="small" variant="light" />;
        default:
            return <Chip color="info" label="unknown" size="small" variant="light" />;
    }
};

export const renterName = rental => {
    if (rental.renter_user) {
        return `${rental.renter_user.first_name} ${rental.renter_user.last_name}`;
    }
    if (rental.renter_invite) {
        return `${rental.renter_invite.first_name} ${rental.renter_invite.last_name}`;
    }
    return '';
};

// react-table sorts by the value `accessor` resolves, not by what `Cell` renders — a
// string accessor like 'name' or a raw Firestore Timestamp under 'start_date' either
// resolves to nothing or stringifies to "[object Object]" for every row, so clicking the
// header does nothing. Accessor functions here return real, comparable values (a name
// string, epoch millis for dates); Cell keeps the human-readable formatting.
const RentalsTable = ({ rentals, onRowClick, loading, showProperty = true }) => {
    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Name',
                id: 'name',
                accessor: renterName,
                Cell: ({ value }) => value || '-'
            },
            showProperty && {
                Header: 'PROPERTY',
                accessor: 'property.full_address'
            },
            {
                Header: 'Start Date',
                id: 'start_date',
                accessor: rental =>
                    typeof rental.start_date?.seconds === 'number'
                        ? rental.start_date.seconds * 1000
                        : null,
                sortType: 'basic',
                Cell: ({ row }) =>
                    convertFromFirestoreTimestamp(row.original.start_date, true)
            },
            {
                Header: 'End Date',
                id: 'end_date',
                accessor: rental =>
                    typeof rental.end_date?.seconds === 'number'
                        ? rental.end_date.seconds * 1000
                        : null,
                sortType: 'basic',
                Cell: ({ row }) =>
                    convertFromFirestoreTimestamp(row.original.end_date, true)
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: StatusCell
            }
        ];
        return cols.filter(Boolean);
    }, [showProperty]);

    return (
        <Table
            columns={columns}
            data={rentals}
            onRowClick={onRowClick}
            loading={loading}
            initialSortBy={[{ id: 'start_date', desc: true }]}
            minWidth={showProperty ? 700 : 550}
        />
    );
};

export default RentalsTable;
